import { useState, useEffect } from "react";
import { Modal, Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import { MDBIcon, MDBSpinner } from "mdb-react-ui-kit";
import { Tag } from "../ui";
import { api } from "../../services";
import { toast } from "react-toastify";
import BranchMapping from "./BranchMapping";
import { useTranslation } from "react-i18next";
import { usePermission } from "../../hooks/usePermission";
import { PERMISSION_ACTIONS, RBAC_MODULES } from "../../config/routePermissions";
import {
  isBranchPartnerManaged,
  branchPartnerAutoAcceptTooltipKey,
} from "../../utils/posReadOnly";

const CustomToggle = ({
  checked,
  onChange,
  disabled,
  activeColor = "#00d69f",
}) => (
  <div
    className={`custom-switch-root ${checked ? "is-checked" : ""} ${
      disabled ? "is-disabled" : ""
    }`}
    style={{ "--active-bg": activeColor }} // Use CSS variable for the color
    onClick={() => !disabled && onChange(!checked)}
  >
    <div className="custom-switch-thumb" />
  </div>
);

const IntegrationMoreModal = ({
  show,
  onHide,
  app,
  latestSync,
  t,
  branchId,
  storeId,
  onSyncChange,
  isFoodicsIntegrated,
  isTabSenseIntegrated,
  branch = null,
  isPending,
  integrationMutationsDisabled = false,
  /** Store mapping dropdown / map-sync — `settings:manage_branch_mappings` only. */
  branchMappingMutationsDisabled = null,
  autoOrderMutationsDisabled = null,
}) => {
  const branchMappingDisabled =
    branchMappingMutationsDisabled !== null
      ? branchMappingMutationsDisabled
      : integrationMutationsDisabled;
  const autoOrderDisabled =
    autoOrderMutationsDisabled !== null
      ? autoOrderMutationsDisabled
      : integrationMutationsDisabled;
  const { i18n } = useTranslation("translation");
  const { can } = usePermission();
  const canPerformSubscription = can(RBAC_MODULES.SUBSCRIPTIONS, PERMISSION_ACTIONS.PERFORM_SUBSCRIPTION);
  const isArabic = i18n.language === "ar";
  const [isConnected, setIsConnected] = useState(!!latestSync && !isPending);
  const [syncedDate, setSyncedDate] = useState(latestSync?.synced_date);
  const [disconnectMarked, setDisconnectMarked] = useState(!!latestSync?.disconnectAtRenewal);
  const [disconnectDate, setDisconnectDate] = useState(null);
  const [disconnectBusy, setDisconnectBusy] = useState(false);

  const displayDate = syncedDate || latestSync?.synced_date;
  const lastSyncTime = displayDate
    ? (() => {
        const d = new Date(displayDate);
        if (isArabic) {
          const formatted = d.toLocaleString("en-US", {
            year: "numeric",
            month: "numeric",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
            second: "numeric",
            hour12: true,
          });
          const LRI = "\u2066";
          const PDI = "\u2069";
          return `${LRI}${formatted}${PDI}`;
        }
        return d.toLocaleString();
      })()
    : "N/A";

  const [isBranchOpen, setIsBranchOpen] = useState(
    !!latestSync?.synced_status && latestSync?.external_id !== null
  );
  const [isAutoAccept, setIsAutoAccept] = useState(
    latestSync?.auto_order_accept || false
  );
  const [isIntegrationEnabled, setIsIntegrationEnabled] = useState(
    !!latestSync
  );
  const [isTogglingSync, setIsTogglingSync] = useState(false);
  const [isTogglingAvailability, setIsTogglingAvailability] = useState(false);
  const [tempSyncData, setTempSyncData] = useState(null);

useEffect(() => {
  // 1. Define the source of truth (Backend prop or local temp data)
  const activeSync = latestSync || tempSyncData;

  // 2. Update Connection Status - disconnected if pending
  setIsConnected(!!activeSync && !isPending);
  setIsIntegrationEnabled(!!activeSync && !isPending);

  // 3. THE DATE SHIELD: Only update state if the backend actually provides a date.
  // If activeSync is null (disconnected), this IF block is skipped,
  // preserving the previous date in the 'syncedDate' state.
  if (activeSync?.synced_date) {
    setSyncedDate(activeSync.synced_date);
  }

  // 4. Update secondary states
  setIsAutoAccept(activeSync?.auto_order_accept || false);

  // 5. Update Branch status only when modal is active
  if (show) {
    setIsBranchOpen(
      !!activeSync?.synced_status && activeSync?.external_id !== null && !isPending
    );
  }

  // Clean up temp data only if latestSync finally catches up from the parent
  if (latestSync) {
    setTempSyncData(null);
  }
}, [show, latestSync, tempSyncData]);
const handleSyncToggle = async (newStatus) => {
  setIsTogglingSync(true);

  try {
    const token = localStorage.getItem("token");
    const payload = {
      appId: app._id,
      branchId: branchId,
      status: newStatus,
    };

    console.log("🔄 Syncing Branch with App:", payload);

    const response = await api.post("/sync-branch", payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const responseData = response.data?.data || response.data;

    console.log("✅ Branch Sync Response:", response.data);

    setIsIntegrationEnabled(newStatus);
    setIsConnected(newStatus);
    if (!newStatus) {
      setIsBranchOpen(false);
    }

    if (newStatus) {
      if (responseData?.synced_date) setSyncedDate(responseData.synced_date);
      setTempSyncData(response.data.data || response.data);
      setIsBranchOpen(!!responseData?.synced_status);
    } else {
      setIsBranchOpen(false);
    }
    toast.success(
      // response.data?.message ||
      `${app.application} ${
        newStatus
          ? t("Branch synced successfully")
          : t("Branch unsynced successfully")
      }`
    );

    // Notify parent to refresh data
    if (onSyncChange) {
      onSyncChange();
    }
  } catch (error) {
    console.error("❌ Error Syncing Branch:", error);
    toast.error(
      // error.response?.data?.message ||
      `${t("Error syncing")} ${app.application}`
    );

    // Revert toggle state on error
    setIsIntegrationEnabled(!newStatus);
    setIsConnected(!newStatus);
  } finally {
    setIsTogglingSync(false);
  }
};

const handleAvailabilityToggle = async (newStatus) => {
  // Optimistic update
  setIsBranchOpen(newStatus);
  setIsTogglingAvailability(true);

  try {
    const token = localStorage.getItem("token");
    const currentSync = tempSyncData || latestSync;

    const payload = {
      appName: app.application,
      externalId: currentSync?.external_id,
      appId: currentSync?.integrationId || currentSync?._id,
      status: newStatus,
    };

    console.log("🔄 Toggling Branch Availability:", payload);

    const response = await api.post(`/branch/${branchId}/status`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });

    console.log("✅ Branch Availability Response:", response.data);

    toast.success(
      // response.data?.message ||
      `${app.application} ${
        newStatus
          ? t("App marked as Available successfully")
          : t("App marked as Unavailable successfully")
      }`
    );

    if (onSyncChange) {
      onSyncChange();
    }
  } catch (error) {
    console.error("❌ Error Toggling Availability:", error);
     toast.error(
        `Failed to update status for ${app.application}. ${
          error.response?.data?.message || "Please try again later."
        }`
      );

    // Revert state on error
    setIsBranchOpen(!newStatus);
  } finally {
    setIsTogglingAvailability(false);
  }
};

const handleAutoOrderAccept = async (
  branchId,
  appIntegrationId,
  externalId,
  autoAccept
) => {
  try {
    const token = localStorage.getItem("token");
    const payload = {
      appIntegrationId,
      externalId,
      autoOrderAccept: autoAccept,
    };

    const response = await api.post(
      `/branch/${branchId}/auto-order-status`,
      payload,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    if (response.status >= 200 && response.status < 300) {
      toast.success(
        //   response.data?.message ||
        t("Auto accept order status updated")
      );
      setIsAutoAccept(autoAccept);
      if (onSyncChange) {
        onSyncChange();
      }
    } else {
      throw new Error(`Server responded with status ${response.status}`);
    }
  } catch (error) {
    console.error("❌ Error updating auto accept order status:", error);
    toast.error(
      `${t("Failed to update auto accept order status.")} ${
        error.response?.data?.message || t("Please try again later.")
      }`
    );
    // Revert state on error
    setIsAutoAccept(!autoAccept);
  }
};

if (!app) return null;

  const isPosBranchMapped = isFoodicsIntegrated || isTabSenseIntegrated;
  const partnerManaged = isBranchPartnerManaged(branch);
  const autoAcceptLocked = isPosBranchMapped || partnerManaged;
  const posAutoAcceptTooltip = partnerManaged
    ? t(branchPartnerAutoAcceptTooltipKey(branch))
    : t("posAutoAcceptDisabledForMappedBranch");

const isWideModal =
  app?.application?.toUpperCase().includes("FOODICS") ||
  app?.application?.toUpperCase().includes("CHEFZ") ||
  app?.application?.toUpperCase().includes("KEETA") ||
  app?.application?.toUpperCase().includes("TABSENSE");

return (
  <>
    {isWideModal && (
      <style>{`
          @media (min-width: 670px) {
            .wide-modal-dialog {
              min-width: 670px !important;
            }
          }
        `}</style>
    )}
    <Modal
      show={show}
      onHide={onHide}
      centered
      className="integration-modal"
      dialogClassName={isWideModal ? "wide-modal-dialog" : ""}
    >
      <Modal.Header
        closeButton
        className="border-0"
        style={{ padding: "30px" }}
      >
        <Modal.Title
          className="fw-bold"
          style={{ color: "#030F1B", fontSize: "20px" }}
        >
          {app.application} {t("integration")}
        </Modal.Title>
      </Modal.Header>

      <hr className="m-0 opacity-25" />

      <Modal.Body className="p-0">
        <div className="px-5 py-2">
          <div className="mb-3 mt-3">
            {/* Status */}
            <div className="d-flex align-items-center mb-3" style={{ gap: 80 }}>
              <span className="fw-bold small" style={{ color: "#030F1B" }}>
                {t("Status")}
              </span>
              <Tag variant={disconnectMarked ? "warning" : isConnected ? "success" : "error"}>
                {disconnectMarked
                  ? t("Connected until", { date: disconnectDate ? new Date(disconnectDate).toLocaleDateString() : "" })
                  : isConnected
                    ? t("Connected")
                    : t("Disconnected")}
              </Tag>
            </div>

            {/* Sync Time */}
            {isConnected && (
              <div className="d-flex align-items-center mb-3" style={{ gap: 59 }}>
                <span className="fw-bold small" style={{ color: "#030F1B" }}>
                  {app.application?.toLowerCase().includes("foodics") ||
                  app.application?.toLowerCase().includes("tabsense")
                    ? t("Last Sync")
                    : t("Linked At")}
                </span>
                <span className="fw-medium small" style={{ color: "#030F1B" }}>
                  {lastSyncTime}
                </span>
              </div>
            )}

            {(app.application?.toLowerCase().includes("jahez") ||
              app.application?.toLowerCase().includes("ninja")) && (
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-2">
                  <span className="fw-bold small" style={{ color: "#030F1B" }}>
                    {t("Enable Integration")}
                  </span>
                  {isTogglingSync && (
                    <MDBSpinner size="sm" className="text-primary" />
                  )}
                </div>
                <CustomToggle
                  checked={isIntegrationEnabled}
                  onChange={(val) => handleSyncToggle(val)}
                  disabled={isTogglingSync || integrationMutationsDisabled}
                  activeColor="#083B4C"
                />
              </div>
            )}

            {/* Branch Mapping Component */}
            <BranchMapping
              app={app}
              branch={branch}
              branchId={branchId}
              storeId={storeId}
              onSyncComplete={onSyncChange}
              isPending={isPending}
              mutationsDisabled={branchMappingDisabled}
            />
          </div>
        </div>

        {/* Bottom Toggles Section - Hidden for POS integrations (Foodics, TabSense) */}
        {!app.application?.toLowerCase().includes("foodics") &&
          !app.application?.toLowerCase().includes("tabsense") && (
          <>
            <hr className="m-0 opacity-25" />
            <div
              className="d-grid gap-3"
              style={{ padding: "24px 33px 44px 33px" }}
            >
              <div
                className={`custom-toggle-container ${
                  isBranchOpen ? "is-active" : ""
                }`}
              >
                <label className="d-flex align-items-center justify-content-between p-3 rounded-4 mb-0">
                  <div className="d-flex align-items-center gap-2">
                    <span className="label-text">
                      {isBranchOpen ? t("Available") : t("Unavailable")}
                    </span>
                    {isTogglingAvailability && (
                      <MDBSpinner size="sm" className="text-primary" />
                    )}
                  </div>
                  {!isConnected ? (
                    /* Tooltip when not connected */
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip>
                          {t("appNotIntegratedTooltip")}
                        </Tooltip>
                      }
                    >
                      {/* We use a span wrapper because disabled elements don't trigger hover events */}
                      <span className="d-inline-block">
                        <CustomToggle
                          checked={isBranchOpen}
                          disabled={true}
                          style={{ pointerEvents: "none" }}
                        />
                      </span>
                    </OverlayTrigger>
                  ) : (
                    /* Normal Toggle Behavior */
                    <CustomToggle
                      checked={isBranchOpen}
                      onChange={(val) => handleAvailabilityToggle(val)}
                      disabled={
                        !isConnected ||
                        isTogglingAvailability ||
                        integrationMutationsDisabled
                      }
                    />
                  )}
                </label>
              </div>

              <div
                className={`custom-toggle-container ${
                  isAutoAccept ? "is-active" : ""
                }`}
              >
                <div className="d-flex align-items-center justify-content-between p-3 rounded-4 mb-0">
                  <span className="label-text">{t("Auto Accept Orders")}</span>

                  {autoAcceptLocked ? (
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip>{posAutoAcceptTooltip}</Tooltip>
                      }
                    >
                      <span className="d-inline-block">
                        <CustomToggle
                          type="switch"
                          checked={isAutoAccept}
                          disabled={true}
                          style={{ pointerEvents: "none" }}
                        />
                      </span>
                    </OverlayTrigger>
                  ) : !isConnected ? (
                    /* Tooltip when not connected */
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip>
                          {t("appNotIntegratedTooltip")}
                        </Tooltip>
                      }
                    >
                      {/* We use a span wrapper because disabled elements don't trigger hover events */}
                      <span className="d-inline-block">
                        <CustomToggle
                          checked={isAutoAccept}
                          disabled={true}
                          style={{ pointerEvents: "none" }}
                        />
                      </span>
                    </OverlayTrigger>
                  ) : autoOrderDisabled ? (
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip dir={i18n.dir()}>
                          {t("autoAcceptOrdersPermissionTooltip")}
                        </Tooltip>
                      }
                    >
                      <span
                        className="d-inline-block"
                        style={{ cursor: "not-allowed" }}
                        tabIndex={0}
                        aria-label={t("autoAcceptOrdersPermissionTooltip")}
                      >
                        <span style={{ pointerEvents: "none" }}>
                          <CustomToggle
                            checked={isAutoAccept}
                            disabled
                          />
                        </span>
                      </span>
                    </OverlayTrigger>
                  ) : (
                    /* Normal Toggle Behavior */
                    <CustomToggle
                      checked={isAutoAccept}
                      onChange={(val) => {
                        const currentSync = tempSyncData || latestSync;
                        handleAutoOrderAccept(
                          branchId,
                          currentSync?.integrationId || currentSync?._id,
                          currentSync?.external_id || branchId,
                          val
                        );
                      }}
                      disabled={!isConnected || autoAcceptLocked}
                    />
                  )}
                </div>
              </div>

              {canPerformSubscription ? (
              <div className={`custom-toggle-container ${disconnectMarked ? "is-active" : ""}`}>
                <div className="d-flex align-items-center justify-content-between p-3 rounded-4 mb-0">
                  <div>
                    <span className="label-text">{t("Disconnect to downgrade")}</span>
                    <div className="small text-muted">
                      {disconnectMarked
                        ? t("Marked stays live", { date: disconnectDate ? new Date(disconnectDate).toLocaleDateString() : "" })
                        : t("Removes this branch at renewal")}
                    </div>
                  </div>
                  <CustomToggle
                    checked={disconnectMarked}
                    disabled={!isConnected || disconnectBusy || integrationMutationsDisabled}
                    activeColor="#c9a227"
                    onChange={async (val) => {
                      setDisconnectBusy(true);
                      try {
                        const res = await api.post("/subscriptions/bundle/disconnect", {
                          branchId,
                          appName: app.application,
                          marked: val,
                        });
                        setDisconnectMarked(!!res.data?.disconnectAtRenewal);
                        setDisconnectDate(res.data?.renewsAt || null);
                      } catch (error) {
                        toast.error(error.response?.data?.message || t("subscriptionPage.saveError"));
                      } finally {
                        setDisconnectBusy(false);
                      }
                    }}
                  />
                </div>
              </div>
              ) : null}
            </div>
          </>
        )}
      </Modal.Body>

      <style jsx>{`
        :global(.integration-modal .modal-content) {
          border: none !important;
          border-radius: 15px !important;
          overflow: hidden;
        }

        /* Container background logic */
        .custom-toggle-container > div,
        .custom-toggle-container > label {
          background-color: #f3f4f6;
          transition: all 0.2s ease-in-out;
          cursor: pointer;
          width: 100%;
          border-radius: 12px !important;
        }

        .custom-toggle-container.is-active > div,
        .custom-toggle-container.is-active > label {
          background-color: #e8f7f0 !important;
        }

        .label-text {
          color: #8c9196;
          font-weight: 500;
          transition: color 0.2s;
        }

        .custom-toggle-container.is-active .label-text {
          color: #00d69f;
          font-weight: 700;
        }

        /* CUSTOM TOGGLE CORE STYLES */
        .custom-switch-root {
          width: 31px;
          height: 17px;
          background-color: #8c8c8c;
          border-radius: 50px;
          position: relative;
          cursor: pointer;
          transition: background-color 0.3s;
          flex-shrink: 0;
        }

        .custom-switch-root.is-checked {
          background-color: var(--active-bg) !important;
        }

        .custom-switch-root.is-disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .custom-switch-thumb {
          width: 13px; /* Sized to fit 17px height with 2px padding */
          height: 13px;
          background-color: white;
          border-radius: 50%;
          position: absolute;
          top: 2px;
          left: 2px;
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
        }

        .is-checked .custom-switch-thumb {
          /* (Track Width 31) - (Thumb Width 13) - (Left Padding 2) - (Right Padding 2) = 14px */
          transform: translateX(14px);
        }

        :global(html[dir="rtl"]) .custom-switch-thumb {
          left: auto;
          right: 2px;
        }

        :global(html[dir="rtl"]) .is-checked .custom-switch-thumb {
          transform: translateX(-14px);
        }

        /* Removing any default bootstrap interference */
        :global(.form-check) {
          padding-left: 0 !important;
          margin-bottom: 0 !important;
        }
      `}</style>
    </Modal>
  </>
);
};

export default IntegrationMoreModal;
